import { add, integer } from '../kernel/primitives.ts';

export type NavPosition = Readonly<{ x: number; y: number; layer: number }>;
export type WalkableCell = Readonly<{ x: number; y: number; blocksSight?: boolean }>;
export type WalkableLayer = Readonly<{ id: number; cells: readonly WalkableCell[] }>;
export type LayerLink = Readonly<{
  from: NavPosition;
  to: NavPosition;
  cost: number;
  bidirectional: boolean;
}>;
export type NavigationMap = Readonly<{
  cellSize: number;
  layers: readonly WalkableLayer[];
  links: readonly LayerLink[];
}>;
export type PathStep = Readonly<{ position: NavPosition; cost: number }>;

const positionKey = ({ x, y, layer }: NavPosition): string => `${layer}:${x}:${y}`;
const compareText = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
const samePosition = (a: NavPosition, b: NavPosition): boolean =>
  a.x === b.x && a.y === b.y && a.layer === b.layer;

function assertPosition(position: NavPosition, label: string): void {
  integer(position.x);
  integer(position.y);
  if (integer(position.layer) < 0) throw new Error(`${label} has an invalid layer`);
}

export function validateNavigationMap(map: NavigationMap): void {
  if (integer(map.cellSize) < 1) throw new Error('Navigation cell size must be positive');
  const cells = new Set<string>();
  const layers = new Set<number>();
  for (const layer of map.layers) {
    if (integer(layer.id) < 0 || layers.has(layer.id)) throw new Error('Navigation layer IDs must be unique non-negative integers');
    layers.add(layer.id);
    for (const cell of layer.cells) {
      assertPosition({ ...cell, layer: layer.id }, 'Walkable cell');
      if (cell.x % map.cellSize || cell.y % map.cellSize) throw new Error('Walkable cells must align to the metric grid');
      const key = positionKey({ ...cell, layer: layer.id });
      if (cells.has(key)) throw new Error(`Duplicate walkable cell ${key}`);
      cells.add(key);
    }
  }
  for (const link of map.links) {
    assertPosition(link.from, 'Layer link');
    assertPosition(link.to, 'Layer link');
    if (integer(link.cost) < 1) throw new Error('Layer links need a positive metric cost');
    if (!cells.has(positionKey(link.from)) || !cells.has(positionKey(link.to))) throw new Error('Layer links must join walkable cells');
  }
}

// Maps are immutable once built, so each is indexed and validated once (hover pathfinding runs per mouse move).
const walkable = new WeakMap<NavigationMap, ReadonlySet<string>>();
const validated = new WeakSet<NavigationMap>();

export function isWalkable(map: NavigationMap, position: NavPosition): boolean {
  let cells = walkable.get(map);
  if (!cells) walkable.set(map, cells = new Set(map.layers.flatMap(layer => layer.cells.map(cell => positionKey({ ...cell, layer: layer.id })))));
  return cells.has(positionKey(position));
}

export function neighbours(map: NavigationMap, position: NavPosition): PathStep[] {
  const sameLayer = [
    { x: position.x - map.cellSize, y: position.y, layer: position.layer },
    { x: position.x, y: position.y - map.cellSize, layer: position.layer },
    { x: position.x, y: position.y + map.cellSize, layer: position.layer },
    { x: position.x + map.cellSize, y: position.y, layer: position.layer },
  ].filter(candidate => isWalkable(map, candidate)).map(candidate => ({ position: candidate, cost: map.cellSize }));
  const linked: PathStep[] = [];
  for (const link of map.links) {
    if (samePosition(link.from, position)) linked.push({ position: { ...link.to }, cost: link.cost });
    if (link.bidirectional && samePosition(link.to, position)) linked.push({ position: { ...link.from }, cost: link.cost });
  }
  return [...sameLayer, ...linked].sort((a, b) => compareText(positionKey(a.position), positionKey(b.position)) || a.cost - b.cost);
}

export function findPath(map: NavigationMap, start: NavPosition, goal: NavPosition, occupied: readonly NavPosition[] = []): PathStep[] | null {
  if (!validated.has(map)) { validateNavigationMap(map); validated.add(map); }
  if (!isWalkable(map, start) || !isWalkable(map, goal)) return null;
  if (samePosition(start, goal)) return [];
  const startKey = positionKey(start);
  const goalKey = positionKey(goal);
  const blocked = new Set(occupied.map(positionKey));
  blocked.delete(startKey);
  if (blocked.has(goalKey)) return null;
  const positions = new Map([[startKey, start]]);
  const costs = new Map([[startKey, 0]]);
  const previous = new Map<string, { key: string; cost: number }>();
  const open = [startKey];
  while (open.length) {
    open.sort((a, b) => costs.get(a)! - costs.get(b)! || compareText(a, b));
    const currentKey = open.shift()!;
    if (currentKey === goalKey) break;
    const current = positions.get(currentKey)!;
    for (const step of neighbours(map, current)) {
      const nextKey = positionKey(step.position);
      if (blocked.has(nextKey)) continue;
      const nextCost = add(costs.get(currentKey)!, step.cost);
      if (costs.has(nextKey) && costs.get(nextKey)! <= nextCost) continue;
      costs.set(nextKey, nextCost);
      positions.set(nextKey, step.position);
      previous.set(nextKey, { key: currentKey, cost: step.cost });
      if (!open.includes(nextKey)) open.push(nextKey);
    }
  }
  if (!previous.has(goalKey)) return null;
  const path: PathStep[] = [];
  let cursor = goalKey;
  while (cursor !== startKey) {
    const edge = previous.get(cursor)!;
    path.push({ position: positions.get(cursor)!, cost: edge.cost });
    cursor = edge.key;
  }
  return path.reverse();
}

export function metricDistance(a: NavPosition, b: NavPosition): number | null {
  if (a.layer !== b.layer) return null;
  return add(Math.abs(a.x - b.x), Math.abs(a.y - b.y));
}

export function hasLineOfSight(map: NavigationMap, from: NavPosition, to: NavPosition): boolean {
  if (from.layer !== to.layer || !isWalkable(map, from) || !isWalkable(map, to)) return false;
  const layer = map.layers.find(candidate => candidate.id === from.layer)!;
  const blocked = new Set(layer.cells.filter(cell => cell.blocksSight).map(cell => `${cell.x}:${cell.y}`));
  let x = from.x / map.cellSize;
  let y = from.y / map.cellSize;
  const targetX = to.x / map.cellSize;
  const targetY = to.y / map.cellSize;
  if (![x, y, targetX, targetY].every(Number.isInteger)) return false;
  const dx = Math.abs(targetX - x);
  const dy = Math.abs(targetY - y);
  const sx = x < targetX ? 1 : -1;
  const sy = y < targetY ? 1 : -1;
  let error = dx - dy;
  while (x !== targetX || y !== targetY) {
    const twice = 2 * error;
    if (twice > -dy) { error -= dy; x += sx; }
    if (twice < dx) { error += dx; y += sy; }
    if (x === targetX && y === targetY) return true;
    const position = { x: x * map.cellSize, y: y * map.cellSize, layer: from.layer };
    if (!isWalkable(map, position) || blocked.has(`${position.x}:${position.y}`)) return false;
  }
  return true;
}
